import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import apiService from '@/services/api';

export default function ProviderLoginPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { setUser, user, loading: authLoading } = useAuth();

  // Redirect already-authenticated providers
  useEffect(() => {
    if (!authLoading && user) {
      const roles = user.roles || (user.role ? [user.role] : []);
      if (roles.includes('provider')) {
        navigate('/provider/dashboard', { replace: true });
      }
    }
  }, [user, authLoading, navigate]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast({
        title: 'Required Fields',
        description: 'Please enter both email and password',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);
      console.log('🔐 ProviderLoginPage: Starting login for', email);
      
      const response = await apiService.post('/auth/provider-login', {
        email,
        password,
      });

      console.log('🔐 ProviderLoginPage: Login response', response);
      console.log('🔐 ProviderLoginPage: Response data:', response.data);
      console.log('🔐 ProviderLoginPage: Token in response:', response.data?.token ? 'YES (' + response.data.token.substring(0, 30) + '...)' : 'NO');

      if (response.success) {
        console.log('🔐 ProviderLoginPage: Login successful, setting token and user');
        
        // Check if user has provider role (roles is an array)
        const isProvider = response.data.user?.roles && Array.isArray(response.data.user.roles) && response.data.user.roles.includes('provider');
        
        if (!isProvider) {
          console.error('🔐 ProviderLoginPage: User does not have provider role, roles:', response.data.user?.roles);
          toast({
            title: 'Access Denied',
            description: 'Provider access required',
            variant: 'destructive',
          });
          return;
        }
        
        // Store token in localStorage
        apiService.setToken(response.data.token);
        console.log('🔐 ProviderLoginPage: Token stored in localStorage');
        console.log('🔐 ProviderLoginPage: Token value:', response.data.token?.substring(0, 20) + '...');
        
        // Verify token is in localStorage IMMEDIATELY
        const storedToken = localStorage.getItem('token');
        if (storedToken) {
          console.log('🔐 ProviderLoginPage: ✅ Token verified in localStorage:', storedToken.substring(0, 20) + '...');
        } else {
          console.error('🔐 ProviderLoginPage: ❌ ERROR - Token NOT found in localStorage after setToken!');
          console.error('🔐 ProviderLoginPage: localStorage contents:', {
            hasToken: localStorage.getItem('token'),
            hasUserData: !!localStorage.getItem('userData'),
            allKeys: Object.keys(localStorage)
          });
        }

        // Store userData in localStorage BEFORE updating auth context
        // This ensures it persists and is available immediately for subsequent requests
        const userData = {
          ...response.data.user,
          role: response.data.user.role || (Array.isArray(response.data.user.roles) && response.data.user.roles[0]) || 'provider'
        };
        localStorage.setItem('userData', JSON.stringify(userData));
        console.log('🔐 ProviderLoginPage: User data stored in localStorage:', userData.email);

        // Update auth context with user immediately - this is the key fix!
        // Without this, auth context stays empty until checkAuth() runs
        if (response.data.user) {
          console.log('🔐 ProviderLoginPage: Setting user in auth context:', response.data.user.email, response.data.user.roles);
          setUser(response.data.user);
          console.log('🔐 ProviderLoginPage: User set in auth context');
        } else {
          console.error('🔐 ProviderLoginPage: No user data in response!');
        }

             toast({
        title: 'Success',
        description: 'Welcome back!',
      });

      console.log('🔐 ProviderLoginPage: Navigating to /provider/dashboard');
      // Small delay to ensure token and context are fully set before navigation
      setTimeout(() => {
        navigate('/provider/dashboard', { replace: true });
      }, 150);
    } else {
      console.error('🔐 ProviderLoginPage: Login failed:', response.message);
      toast({
        title: 'Login Failed',
        description: response.message || 'Invalid email or password',
        variant: 'destructive',
      });
    }
  } catch (error: any) {
    console.error('🔐 ProviderLoginPage: Error during login:', error);
    toast({
      title: 'Login Failed',
      description: error.message || 'Invalid email or password',
      variant: 'destructive',
    });
  } finally {
    setLoading(false);
  }
};  // ← Close the handleLogin function
  return (
    <Layout>
      <Helmet>
        <title>Provider Login - VireWire</title>
        <meta name="description" content="Login to your VireWire provider account" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Provider Portal</h1>
            <p className="text-gray-600">Manage your jobs and earnings</p>
          </div>

          {/* Login Card */}
          <Card className="shadow-xl">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg">
              <CardTitle>Sign In</CardTitle>
              <CardDescription className="text-blue-100">
                Access your account to manage jobs
              </CardDescription>
            </CardHeader>

            <CardContent className="p-6">
              <form onSubmit={handleLogin} className="space-y-4">
                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="font-semibold">
                    Email Address
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <Label htmlFor="password" className="font-semibold">
                    Password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 mt-6"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </form>

              {/* Admin Registration Notice */}
              <div className="relative my-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-center text-blue-800 text-sm">
                  <span className="font-semibold">ℹ️ Provider Registration</span>
                  <br/>
                  Provider accounts are registered by administrators only.
                  <br/>
                  Please contact your administrator if you need to register as a provider.
                </p>
              </div>

              {/* Features */}
              <div className="mt-8 pt-6 border-t border-gray-200 space-y-3">
                <h3 className="font-semibold text-gray-900 mb-4">As a Provider, you can:</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    Accept and manage job requests
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    Update job status in real-time
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    Upload photos and documents
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    Track earnings and payments
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    View comprehensive earnings statements
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Support */}
          <p className="text-center text-gray-600 text-sm mt-6">
            Need help?{' '}
            <a href="mailto:support@virewire.com" className="text-blue-600 hover:text-blue-700">
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </Layout>
  );
}
